<script lang="ts">
    import dayjs from 'dayjs';
    import type { DateValue } from '@internationalized/date';
    import { createDatePicker } from '@melt-ui/svelte';
    import {
        VpChevronLeftIcon,
        VpChevronRightIcon,
    } from '@decathlon-experience/icons/svelte';

    import cn from '$common/utils/cn';

    type DatePickerType = ReturnType<typeof createDatePicker>;

    // Define props with types from createDatePicker
    export let calendar: DatePickerType['elements']['calendar'];
    export let prevButton: DatePickerType['elements']['prevButton'];
    export let heading: DatePickerType['elements']['heading'];
    export let headingValue: DatePickerType['states']['headingValue'];
    export let nextButton: DatePickerType['elements']['nextButton'];
    export let months: DatePickerType['states']['months'];
    export let grid: DatePickerType['elements']['grid'];
    export let weekdays: DatePickerType['states']['weekdays'];
    export let isDateDisabled: DatePickerType['helpers']['isDateDisabled'];
    export let isDateUnavailable: DatePickerType['helpers']['isDateUnavailable'];
    export let cell: DatePickerType['elements']['cell'];

    export let numberOfMonths = 1;
    export let monthsOrientation: 'vertical' | 'horizontal' = 'horizontal';
    export let getDayAdditionalStyle:
        | ((date: DateValue) => string)
        | undefined = undefined;
    export let getDayAdditionalContent:
        | ((date: DateValue) => string)
        | undefined = undefined;
    export let overflowContainer: HTMLElement | null = null;
    export let onPrevButton = () => {};
    export let onNextButton = () => {};
</script>

<div {...$calendar} use:calendar class={$$props?.class ?? ''}>
    {#if monthsOrientation === 'vertical'}
        <button
            type="button"
            {...$prevButton}
            use:prevButton
            class="vp-button vp-button--secondary vp-button--small w-full disabled:hidden"
        >
            Afficher les mois précédents
        </button>
    {/if}
    {#if monthsOrientation === 'horizontal'}
        <div class="flex items-center justify-center pb-xs">
            <button
                type="button"
                {...$prevButton}
                use:prevButton
                on:click={onPrevButton}
            >
                <VpChevronLeftIcon class="size-xl" />
            </button>
            <div
                {...$heading}
                use:heading
                class="flex w-full justify-between gap-x-8 text-sm capitalize text-content-neutral"
            >
                {#if numberOfMonths >= 2}
                    <div class="flex-1 text-center font-semibold">
                        {$headingValue.split('-')[0]}
                    </div>
                    <div class="flex-1 text-center font-semibold">
                        {$headingValue.split('-')[1]}
                    </div>
                {:else}
                    <div class="flex-1 text-center font-semibold">
                        {$headingValue}
                    </div>
                {/if}
            </div>
            <button
                type="button"
                {...$nextButton}
                use:nextButton
                on:click={onNextButton}
            >
                <VpChevronRightIcon class="size-xl" />
            </button>
        </div>
    {/if}
    <div
        class={cn(
            numberOfMonths >= 2 && 'flex justify-center gap-x-8',
            monthsOrientation === 'vertical' && 'flex-col justify-start gap-0',
        )}
    >
        {#each $months as month (month.value)}
            {#if monthsOrientation === 'vertical'}
                <div
                    {...$heading}
                    use:heading
                    class="mt-m w-full pb-xs text-center text-sm font-semibold capitalize text-content-neutral"
                >
                    {dayjs(month.value.toString()).format('MMMM YYYY')}
                </div>
            {/if}
            <table {...$grid} use:grid>
                <thead aria-hidden="true">
                    <tr class="">
                        {#each $weekdays as day (day)}
                            <th
                                class="text-center text-xs font-normal leading-4 text-content-neutral"
                            >
                                {day}
                            </th>
                        {/each}
                    </tr>
                </thead>
                <tbody>
                    {#each month.weeks as days (days)}
                        <tr>
                            {#each days as date (date)}
                                <td
                                    class={cn('text-center text-xs leading-4')}
                                    role="gridcell"
                                    aria-disabled={$isDateDisabled(date) ||
                                        $isDateUnavailable(date)}
                                >
                                    <div
                                        {...$cell(date, month.value)}
                                        use:cell
                                        class={cn(
                                            'flex flex-col items-center justify-center',
                                        )}
                                    >
                                        <div class="font-semibold text-inherit">
                                            {date.day}
                                        </div>
                                        {#if !$isDateDisabled(date) || !$isDateUnavailable(date)}
                                            <div
                                                class={cn(
                                                    'whitespace-nowrap text-[10px]',
                                                    getDayAdditionalStyle !=
                                                        null &&
                                                        getDayAdditionalStyle(
                                                            date,
                                                        ),
                                                )}
                                            >
                                                {#if getDayAdditionalContent != null}
                                                    {getDayAdditionalContent(
                                                        date,
                                                    )}
                                                {/if}
                                            </div>
                                        {/if}
                                    </div>
                                </td>
                            {/each}
                        </tr>
                    {/each}
                </tbody>
            </table>
        {/each}
    </div>
    {#if monthsOrientation === 'vertical'}
        <button
            type="button"
            {...$nextButton}
            use:nextButton
            on:click={() => {
                overflowContainer?.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            class="vp-button vp-button--secondary vp-button--small w-full"
        >
            Afficher les mois suivants
        </button>
    {/if}
</div>

<style lang="postcss">
    [data-melt-popover-content] {
        @apply z-50 border border-solid border-bord-quiet bg-white;
    }
    [data-melt-calendar] {
        @apply w-full;
    }

    table {
        @apply mx-auto border-spacing-3xs border-spacing-y-xs;
        border-collapse: separate;
    }

    td:has([data-melt-calendar-cell]) {
        @apply size-3xl p-0 desktop:size-4xl;
    }

    [data-melt-calendar-cell] {
        @apply size-3xl p-0 desktop:size-4xl desktop:hover:bg-container-commercial-neutral;
    }

    [data-melt-calendar-cell][data-disabled] {
        @apply pointer-events-none cursor-default border-white bg-white text-content-quiet opacity-30;
    }

    [data-melt-calendar-cell][data-unavailable] {
        @apply pointer-events-none cursor-default text-content-quiet;
    }

    [data-melt-calendar-cell][data-selected] {
        @apply border-border-brand bg-blue-brand text-white;
    }

    [data-melt-calendar-cell][data-selected][data-disabled] {
        @apply pointer-events-none cursor-default border-white bg-white text-content-quiet;
    }

    [data-melt-calendar-cell][data-outside-visible-months] {
        @apply pointer-events-none cursor-default text-content-quiet opacity-30;
    }

    [data-outside-month] {
        @apply hidden;
    }

    [data-highlighted] {
        @apply bg-container-commercial-neutral;
    }
</style>
